import TableFragment from "./TableFragment";

/**
 * Извлечь значение свойства всех элементов в массиве.
 *
 * Пусть дан массив arr и функция f, вычисляющая некоторое свойство элемента массива.
 * Предполагается, что каждый элемент x в массиве arr имеет одинаковое значение f(x).
 * Функция getProperty(arr, f) извлекает это значение и проверяет, что оно действительно одинаковое для всех элементов.
 * Если же для каких-то двух элементов x1 и x2 массива arr окажется, что f(x1) != f(x2), функция кидает исключение.
 * Сообщение формируется по строковому шаблону, в котором "%values" заменяется на строковое представление элементов.
 *
 * @template A - тип элемента массива
 * @template B - тип свойства элемента
 * @param {A[]} arr - массив
 * @param {(x: A) => B} f - свойство
 * @param {string} template - шаблон сообщения об ошибке
 * @return {B}
 */
function getProperty(arr, f, template = "Elements of the array %values are expected to be equal") {
  if (!arr.length) throw new Error('An array must be non-empty');
  const values = arr.map(f);
  const v = values[0];
  const compareValues = (v, u) => v === u || JSON.stringify(v) === JSON.stringify(u);
  if (values.every((u) => compareValues(v, u))) {
    return v;
  }
  const valuesStr = values.map((x) => '\n- ' + JSON.stringify(x)).join('') + '\n';
  throw new Error(template.replace(/%values/, valuesStr));
}

/**
 * Посчитать аккумулированные суммы чисел из массива чисел.
 * Функция возвращает объект `{ sums, totalSum }` из двух компонентов:
 * массива из частичных сумм той же длины, что входной массив, и полную сумму всех элементов.
 *
 * @example
 * console.log(accumulate([a, b, c])) // -> { sums: [0, a, a + b], totalSum: a + b + c }
 *
 * @param {number[]} arr - массив чисел
 * @return {{ sums: number[], totalSum: number }}
 */
function accumulate(arr) {
  let totalSum = 0;
  const sums = [];
  for (const value of arr) {
    sums.push(totalSum);
    totalSum += value;
  }
  return { sums, totalSum };
}

/**
 * Объединение прямоугольной таблицы из согласующихся по размерам фрагментов.
 * @param {ExcelTableFragment[][]} fragments - таблица из фрагментов
 * @return {ExcelTableFragment} объединённая таблица
 */
export default function mergeFragments(fragments) {
  // количество фрагментов по вертикали
  const fragmentVerticalCount = fragments.length;
  if (fragmentVerticalCount === 0) {
    throw new Error('mergeGrid requires non-empty matrix');
  }
  // количество фрагментов по горизонтали
  const fragmentHorizontalCount = getProperty(fragments, (frags) => frags.length, 'fragments must be a rectangular matrix, but rows\' widths are %values');
  if (fragmentHorizontalCount === 0) {
    throw new Error('mergeGrid requires non-empty matrix');
  }
  // массив ширин (т.е. количества столбцов)
  const columnCounts = getProperty(fragments, (frags) => frags.map((frag) => frag.n), 'Array of n must be the same for each row of fragments: %values');
  // массив высот (т.е. количества строк)
  const lineCounts = fragments.map((fs) => getProperty(fs, (frag) => frag.m, 'Value of m must be the same for each fragment in a row: %values'));
  // offsetX — полное число столбцов, totalColumnCount — массив количеств столбцов, предшествующих каждому фрагменту
  const { sums: offsetX, totalSum: totalColumnCount } = accumulate(columnCounts);
  // offsetY — полное число строк, totalLineCount — массив количеств строк, предшествующих каждому фрагменту
  const { sums: offsetY, totalSum: totalLineCount } = accumulate(lineCounts);

  // мутабельные массивы, которые составляют результат слияния фрагментов
  const data = Array(totalLineCount).fill(0).map(() => Array(totalColumnCount).fill(1));
  const markdown = [];
  const columnWidth = Array(totalColumnCount);
  const rowHeight = Array(totalLineCount);

  for (let i = 0; i < fragmentHorizontalCount; ++i) {
    for (let j = 0; j < fragmentVerticalCount; ++j) {
      const frag = fragments[j][i];
      try {
        TableFragment.validate(frag);
      } catch (e) {
        console.error("Invalid TableFragment", frag);
        throw e;
      }
      // положение левой верхней ячейки во фрагменте относительно объединённой таблицы
      const x0 = offsetX[i];
      const y0 = offsetY[j];
      for (let dx = 0; dx < frag.n; ++dx) {
        for (let dy = 0; dy < frag.m; ++dy) {
          data[y0 + dy][x0 + dx] = frag.data[dy][dx];
        }
      }
      markdown.push(...frag.markdown.map((md) => md.shift(x0, y0)));
      for (let dx = 0; dx < frag.n; ++dx) {
        if (frag.columnWidth[dx] === undefined) continue;
        if (columnWidth[x0 + dx] !== undefined && JSON.stringify(columnWidth[x0 + dx]) !== JSON.stringify(frag.columnWidth[dx]))
          throw new Error(`columnWidth mismatch: ${JSON.stringify(columnWidth[x0 + dx])} and ${JSON.stringify(frag.columnWidth[dx])}`);
        columnWidth[x0 + dx] = frag.columnWidth[dx];
      }
      for (let dy = 0; dy < frag.m; ++dy) {
        if (frag.rowHeight[dy] === undefined) continue;
        if (rowHeight[y0 + dy] !== undefined && JSON.stringify(rowHeight[y0 + dy]) !== JSON.stringify(frag.rowHeight[dy]))
          throw new Error(`rowHeight mismatch: ${JSON.stringify(rowHeight[y0 + dy])} and ${JSON.stringify(frag.rowHeight[dy])}`);
        rowHeight[y0 + dy] = frag.rowHeight[dy];
      }
    }
  }

  return new TableFragment([totalColumnCount, totalLineCount], { data, markdown, columnWidth, rowHeight });
}

/*
// ТЕСТОВЫЙ ПРИМЕР

const frag1 = new TableFragment([3, 2], {
  data: [[0, 1, 2], [3, 4, 5]],
  markdown: [],
  columnWidth: [{}, {}, {}],
  rowHeight: [{}, {}],
});

const frag2 = new TableFragment([2, 2], {
  data: [[10, 11], [13, 14]],
  markdown: [],
  columnWidth: [{}, {}],
  rowHeight: [{}, {}],
});

const frag3 = new TableFragment([3, 1], {
  data: [[6, 7, 8]],
  markdown: [],
  columnWidth: [{}, {}, {}],
  rowHeight: [{}],
});

const frag4 = new TableFragment([2, 1], {
  data: [[0, 0]],
  markdown: [],
  columnWidth: [{}, {}],
  rowHeight: [{}],
});

const fragm = mergeFragments([
  [frag1, frag2],
  [frag3, frag4],
]);

console.warn(fragm);
*/
